import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Bell, CheckCircle2 } from "lucide-react";

const severityClass = {
    urgent: "border-rose-200 bg-rose-50 text-rose-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    info: "border-slate-200 bg-white text-slate-900",
};

export default function Notifications() {
    const [items, setItems] = useState([]);
    const [prefs, setPrefs] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [res, prefsRes] = await Promise.all([
                api.get("/notifications"),
                api.get("/notification-preferences"),
            ]);
            setItems(res.data || []);
            setPrefs(prefsRes.data || null);
        } catch {
            toast.error("Failed to load notifications");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const markRead = async (item) => {
        try {
            await api.post(`/notifications/${encodeURIComponent(item.id)}/read`);
            setItems((prev) => prev.map((n) => n.id === item.id ? { ...n, is_read: true } : n));
        } catch {
            toast.error("Could not mark notification read");
        }
    };

    const unread = items.filter((item) => !item.is_read).length;

    const savePrefs = async (e) => {
        e.preventDefault();
        try {
            const res = await api.put("/notification-preferences", prefs);
            setPrefs(res.data);
            toast.success("Notification preferences saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save preferences");
        }
    };

    return (
        <div className="space-y-6" data-testid="notifications-page">
            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Inbox</div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Notifications</h1>
                <p className="text-sm text-slate-500 mt-1">
                    In-app reminders for case updates, agency responses, triage due dates, retention reviews, and household sharing approvals.
                </p>
            </div>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-slate-200 rounded-lg bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Unread</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{unread}</div>
                </div>
                <div className="border border-slate-200 rounded-lg bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Urgent</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{items.filter((i) => i.severity === "urgent" && !i.is_read).length}</div>
                </div>
                <div className="border border-slate-200 rounded-lg bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Total</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{items.length}</div>
                </div>
            </section>

            {prefs && (
                <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="notification-preferences-section">
                    <div className="text-xs uppercase tracking-widest text-slate-500">External delivery preferences</div>
                    <form onSubmit={savePrefs} className="space-y-4 mt-4">
                        <div className="flex flex-wrap gap-4">
                            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                <input type="checkbox" checked={prefs.email_enabled} onChange={(e) => setPrefs({ ...prefs, email_enabled: e.target.checked })} />
                                Email
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                <input type="checkbox" checked={prefs.whatsapp_enabled} onChange={(e) => setPrefs({ ...prefs, whatsapp_enabled: e.target.checked })} />
                                WhatsApp
                            </label>
                        </div>
                        <input
                            value={prefs.whatsapp_number || ""}
                            onChange={(e) => setPrefs({ ...prefs, whatsapp_number: e.target.value })}
                            placeholder="WhatsApp number, e.g. +447..."
                            className="w-full md:w-80 border border-slate-300 rounded-md px-3 py-2 text-sm"
                            data-testid="notification-whatsapp-number"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {Object.entries(prefs.types || {}).map(([key, enabled]) => (
                                <label key={key} className="inline-flex items-center gap-2 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(enabled)}
                                        onChange={(e) => setPrefs({ ...prefs, types: { ...prefs.types, [key]: e.target.checked } })}
                                    />
                                    {key.replaceAll("_", " ")}
                                </label>
                            ))}
                        </div>
                        <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="save-notification-preferences">
                            Save preferences
                        </Button>
                    </form>
                </section>
            )}

            <section className="space-y-3" data-testid="notifications-list">
                {loading && <div className="text-sm text-slate-500">Loading...</div>}
                {!loading && items.length === 0 && (
                    <div className="border border-slate-200 rounded-lg bg-white p-5 text-sm text-slate-500">
                        No reminders or approval notifications right now.
                    </div>
                )}
                {items.map((item) => (
                    <div
                        key={item.id}
                        className={`border rounded-lg p-4 ${severityClass[item.severity] || severityClass.info} ${item.is_read ? "opacity-60" : ""}`}
                        data-testid={`notification-${item.id}`}
                    >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex items-start gap-3">
                                <Bell size={18} className="mt-0.5" />
                                <div>
                                    <div className="font-heading text-base font-semibold">{item.title}</div>
                                    <p className="text-sm mt-1">{item.body}</p>
                                    <div className="text-xs mt-2 opacity-75">
                                        {item.due_at ? `Due ${item.due_at}` : item.created_at ? new Date(item.created_at).toLocaleString() : item.type}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {item.href && (
                                    <Button asChild variant="outline" size="sm">
                                        <Link to={item.href}>Open</Link>
                                    </Button>
                                )}
                                {!item.is_read && (
                                    <Button type="button" variant="outline" size="sm" onClick={() => markRead(item)} data-testid={`mark-read-${item.id}`}>
                                        <CheckCircle2 size={14} className="mr-1" /> Done
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}
